import { Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core';
import { CheckCircle, Error, List as ListIcon, Search } from '@material-ui/icons';
import { ScannerConnectStatus, useSPPScannerContext, useScannerConnect } from 'components/Scanner/GlobalSPPScanner';
import { ScannerStatusIndicator } from 'components/Scanner/_shared/ScannerStatusIndicator';
import { ScannerLogContextType } from 'entities/ScannerLogContextType';
import { AppManagerRoles } from 'entities/UserRole';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Confirm, useNotify, usePermissions, useTranslate } from 'react-admin';
import { isPlaceAddress } from 'utils/scanner/codeDetectors';
import { logScannerAction } from 'utils/scanner/logScannerAction';
import { useGS1BarcodeResolver } from 'utils/scanner/useGS1BarcodeResolver';

import {
  InventorizationIncorrectStatus,
  InventorizationStatus,
} from '../../../entities/InventorizationStatus';
import { useInitialInventorizationPallets } from '../hooks/useInitialInventorizationPallets';
import { useInventorizationIncomingOrders } from '../hooks/useInventorizationIncomingOrders';
import { CreateStatusParams, PalletMatch } from '../hooks/useInventorizationStatuses';
import { useScannerInput } from '../hooks/useScannerInput';
import {
  InitialInventorizationPallet,
  InventorizationStatusData,
  RemainingPalletsPreview,
  configApi,
} from '../services/configApi';
import { Place, StorageEntityInfo } from '../types/warehouse.types';
import {
  NOTE_MAX_LENGTH,
  getDetailedStatusLabel,
  getIncorrectStatusLabel,
  getStatusColor,
  getStatusLabel,
} from '../utils/inventorizationHelpers';
import { getMatchedPalletOrder } from '../utils/inventorizationOrder';
import {
  isCodeCeelNumber,
  getStatusForPalletScan,
  getStatusForPlaceScan,
} from '../utils/inventorizationScan';
import { InfoBox } from './InfoBox';
import { InitialPalletSearchDialog } from './InitialPalletSearchDialog';
import { InventorizationGridDialog } from './InventorizationGridDialog';
import { InventorizationMode, InventorizationModeToggle } from './InventorizationModeToggle';
import { InventorizationRecordDialog } from './InventorizationRecordDialog';
import { InventorizationRecordsList } from './InventorizationRecordsList';
import { PlaceInfo } from './PlaceInfo';
import { TimedConfirmDialog } from './TimedConfirmDialog';
import { RemainingPalletsDialog } from './RemainingPalletsDialog';

const SSCC_LENGTH = 18;
const CANONICAL_SSCC_RE = new RegExp(`^\\d{${SSCC_LENGTH}}$`);
const INVENTORIZATION_TAB = 'inventorization-tab';
const NORMAL_AUTO_CONFIRM_MS = 5000;
const QUICK_AUTO_CONFIRM_MS = 1000;
const PLACE_SCANS_TO_REPORT_EMPTY = 2;
const PLACE_SCANS_TO_CLEAR = 3;

const getErrorMessage = (err: any, translate: (key: string) => string): string => {
  if (err?.status === 0 || !err?.status) {
    return translate('osfc.storage_ui.inventorization.network_error');
  }
  return err?.body?.message || err?.message || translate('osfc.storage_ui.inventorization.save_failed');
};

interface PendingConfirmData {
  status: InventorizationStatus;
  incorrectStatus?: InventorizationIncorrectStatus;
  sscc: string | null;
  scannedCode: string;
  messageKey: string;
  nextPlace?: Place;
}

type StoreStatusPayload =
  Pick<PendingConfirmData, 'status' | 'incorrectStatus' | 'sscc' | 'nextPlace'> & {
    /** The note of the record being written — not necessarily the one on screen. */
    note?: string;
  };

interface ManualStatusOption {
  status: InventorizationStatus;
  incorrectStatus?: InventorizationIncorrectStatus;
}

/**
 * The record the panel works on: the pallet that was just scanned, or — with nothing
 * scanned — the one recorded last.
 */
const findRecordToSelect = (
  records: InventorizationStatusData[],
  scannedSscc?: string | null
): InventorizationStatusData | null => {
  const scanned = scannedSscc
    ? records.find((record) => record.sscc === scannedSscc)
    : undefined;

  return scanned ?? records[records.length - 1] ?? null;
};

/** The statuses an operator can record by hand, in the order they are offered. */
const MANUAL_STATUS_OPTIONS: ManualStatusOption[] = [
  { status: InventorizationStatus.correct },
  {
    status: InventorizationStatus.incorrect,
    incorrectStatus: InventorizationIncorrectStatus.place_empty,
  },
  {
    status: InventorizationStatus.incorrect,
    incorrectStatus: InventorizationIncorrectStatus.pallet_missing,
  },
  {
    status: InventorizationStatus.incorrect,
    incorrectStatus: InventorizationIncorrectStatus.wrong_pallet,
  },
];

interface InventorizationSidePanelProps {
  selectedPlaces: Place[];
  terminalId: number;
  storageEntities: StorageEntityInfo[];
  findPlace: (code: string) => Place | null;
  onPlaceSelect: (place: Place) => void;
  onPlaceDeselect: () => void;
  createStatus?: (params: CreateStatusParams) => Promise<void>;
  markPlaceEmpty?: (placeId: number) => Promise<void>;
  recordScannedPallet?: (placeId: number, sscc: string, match?: PalletMatch) => Promise<void>;
  deleteStatus?: (statusId: number) => Promise<void>;
  setPlaceStatusFromRecords?: (placeId: number, records: InventorizationStatusData[]) => void;
  clearPlace?: (placeId: number) => Promise<void>;
  placeAddressTemplates?: string[];
}

export const InventorizationSidePanel: React.FC<InventorizationSidePanelProps> = ({
  selectedPlaces,
  terminalId,
  storageEntities,
  placeAddressTemplates,
  findPlace,
  onPlaceSelect,
  onPlaceDeselect,
  createStatus,
  markPlaceEmpty,
  recordScannedPallet,
  deleteStatus,
  setPlaceStatusFromRecords,
  clearPlace,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  // Mirrors the endpoint ACL (admin, app_manager, osfc_manager)
  const canGenerateRemainingPallets =
    permissions && AppManagerRoles.includes(permissions.role);
  const [records, setRecords] = useState<InventorizationStatusData[]>([]);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  // The record whose fields the operator opened, to correct the counted boxes.
  const [detailsRecord, setDetailsRecord] = useState<InventorizationStatusData | null>(null);
  const [note, setNote] = useState<string>('');
  const [sscc, setSscc] = useState<string>('');
  // The SSCC waiting for a pallet number, because no pallet carries it.
  const [unmatchedSscc, setUnmatchedSscc] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [manualCode, setManualCode] = useState<string>('');
  const [gridDialogOpen, setGridDialogOpen] = useState(false);
  const [placeNotFound, setPlaceNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecordsLoading, setIsRecordsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingDialogOpen, setRemainingDialogOpen] = useState(false);
  const [remainingPreview, setRemainingPreview] = useState<RemainingPalletsPreview | null>(null);
  const [isRemainingLoading, setIsRemainingLoading] = useState(false);
  const [isRemainingGenerating, setIsRemainingGenerating] = useState(false);
  const notify = useNotify();
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirmData | null>(null);
  const [showClearPlacePending, setShowClearPlacePending] = useState(false);
  const [mode, setMode] = useState<InventorizationMode>(InventorizationMode.initial);

  // How many times the same place code has been scanned in a row, which is what initial mode
  // acts on. A ref, because a scan must see the count the previous scan left behind.
  const placeScanStreakRef = useRef<{ placeId: number; count: number } | null>(null);

  const isQuickMode = mode === InventorizationMode.quick;
  const isInitialMode = mode === InventorizationMode.initial;
  const isNormalMode = mode === InventorizationMode.normal;

  const { registerScanHandler, registerTargetPage } = useSPPScannerContext();
  const {
    isConnected,
    isDeviceAuthorized,
    isReconnecting,
    connect: connectScanner,
  } = useScannerConnect();

  const { tryScan: tryGS1Resolve, isResolving } = useGS1BarcodeResolver();

  const {
    findPalletBySscc,
    findPalletByExtId,
    isExtIdAmbiguous,
    isLoading: isPalletsDataLoading,
    hasLoadFailed: hasPalletsDataFailed,
    reload: reloadPalletsData,
  } = useInitialInventorizationPallets(terminalId, isInitialMode);

  const {
    orders: incomingOrders,
    findOrderById: findIncomingOrderById,
    isLoading: isIncomingOrdersLoading,
    hasLoadFailed: haveIncomingOrdersFailed,
    reload: reloadIncomingOrders,
  } = useInventorizationIncomingOrders(terminalId);

  const selectedPlace = selectedPlaces.length === 1 ? selectedPlaces[0] : null;

  const palletSSCCs = useMemo(
    () => storageEntities.map(e => e.pallet_sscc).filter(Boolean),
    [storageEntities]
  );
  const ceelNumbers = useMemo(
    () => storageEntities.map(e => e.ceel_number).filter(Boolean),
    [storageEntities]
  );

  const isPlaceEmpty = useMemo(
    () => !storageEntities.some((entity) => entity.pallet_sscc || entity.ceel_number),
    [storageEntities]
  );

  const selectedRecord = useMemo(
    () => records.find((record) => record.id === selectedRecordId) ?? null,
    [records, selectedRecordId]
  );

  /** The note and SSCC fields always describe the record the status buttons write to. */
  const selectRecord = useCallback((record: InventorizationStatusData | null) => {
    setSelectedRecordId(record?.id ?? null);
    setNote(record?.note ?? '');
    setSscc(record?.sscc ?? '');
  }, []);

  // Guards against a reply for a place the operator has already left.
  const loadRequestRef = useRef(0);

  const loadRecords = useCallback(async (
    placeId: number,
    scannedSscc?: string | null
  ): Promise<InventorizationStatusData[] | null> => {
    const requestId = ++loadRequestRef.current;
    setIsRecordsLoading(true);
    try {
      const data = await configApi.getInventorizationStatuses(terminalId, placeId);
      if (requestId !== loadRequestRef.current) return null;
      setRecords(data);
      selectRecord(findRecordToSelect(data, scannedSscc));
      return data;
    } catch (err) {
      console.error('Failed to fetch inventorization statuses', err);
      return null;
    } finally {
      // A superseded reply leaves the mask to the request that replaced it.
      if (requestId === loadRequestRef.current) setIsRecordsLoading(false);
    }
  }, [terminalId, selectRecord]);

  const logScan = useCallback((code: string) => {
    logScannerAction(code, {
      contextType: ScannerLogContextType.inventorization,
      terminalId,
    });
  }, [terminalId]);

  const selectPlaceByCode = useCallback((code: string): boolean => {
    const place = findPlace(code);
    setPlaceNotFound(!place);
    if (place) onPlaceSelect(place);
    return !!place;
  }, [findPlace, onPlaceSelect]);

  const handleSearchSubmit = useCallback(() => {
    const code = searchValue.trim();
    if (!code) return;
    
    if (selectPlaceByCode(code)) {
      setSearchValue('');
    }
  }, [searchValue, selectPlaceByCode]);

  const runPlaceAction = useCallback(async (action: () => Promise<void>): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err) {
      console.error('Failed to store inventorization status:', err);
      setError(getErrorMessage(err, translate));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [translate]);

  const storeStatus = useCallback(async (payload: StoreStatusPayload) => {
    if (!selectedPlace || !createStatus) return;

    const stored = await runPlaceAction(() => createStatus({
      placeId: selectedPlace.id,
      status: payload.status,
      incorrectStatus: payload.incorrectStatus,
      note: payload.note || undefined,
      sscc: payload.sscc,
      isPlaceEmpty,
    }));
    if (!stored) return;

    // The selection follows the scanner: another place takes over, anything else leaves the
    // place on screen with what has been recorded on it. Only the Deselect button clears it.
    if (payload.nextPlace && payload.nextPlace.id !== selectedPlace.id) {
      onPlaceSelect(payload.nextPlace);
    } else {
      await loadRecords(selectedPlace.id, payload.sscc);
    }
  }, [selectedPlace, createStatus, isPlaceEmpty, loadRecords, onPlaceSelect, runPlaceAction]);

  /** A scan writes to the record of the pallet scanned, which keeps the note it already has. */
  const getNoteForScannedPallet = useCallback(
    (scannedSscc: string | null): string | undefined => (
      (scannedSscc ? records.find((record) => record.sscc === scannedSscc)?.note : null) ?? undefined
    ),
    [records]
  );

  const handleManualStatus = useCallback((
    newStatus: InventorizationStatus,
    newIncorrectStatus?: InventorizationIncorrectStatus
  ) => {
    const trimmedSscc = sscc.trim();
    if (trimmedSscc && !CANONICAL_SSCC_RE.test(trimmedSscc)) {
      setError(translate('osfc.storage_ui.inventorization.invalid_sscc'));
      return;
    }
    storeStatus({
      status: newStatus,
      incorrectStatus: newIncorrectStatus,
      sscc: trimmedSscc || null,
      note,
    });
  }, [sscc, note, storeStatus, translate]);

  const handleConfirmPending = useCallback(() => {
    if (!pendingConfirm) return;
    setPendingConfirm(null);
    if (pendingConfirm.sscc) {
      setSscc(pendingConfirm.sscc);
    }
    storeStatus({ ...pendingConfirm, note: getNoteForScannedPallet(pendingConfirm.sscc) });
  }, [pendingConfirm, getNoteForScannedPallet, storeStatus]);

  const handleCancelPending = useCallback(() => setPendingConfirm(null), []);

  /**
   * Only a hint for the error message: configs published before place-address
   * templates existed have none, and a stale config must not block a scan.
   */
  const looksLikePlaceCode = useCallback((code: string): boolean => {
    try {
      return isPlaceAddress(code, placeAddressTemplates);
    } catch {
      return false;
    }
  }, [placeAddressTemplates]);

  const reportUnknownCode = useCallback((code: string) => {
    if (looksLikePlaceCode(code)) {
      setPlaceNotFound(true);
      return;
    }
    setError(translate(placeAddressTemplates?.length
      ? 'osfc.storage_ui.inventorization.unknown_code'
      : 'osfc.storage_ui.inventorization.no_place_templates'));
  }, [looksLikePlaceCode, placeAddressTemplates, translate]);

  const reportPlaceEmpty = useCallback(async (placeId: number) => {
    if (!markPlaceEmpty) return;

    if (await runPlaceAction(() => markPlaceEmpty(placeId))) {
      await loadRecords(placeId);
    }
  }, [markPlaceEmpty, runPlaceAction, loadRecords]);

  const storeScannedPallet = useCallback(async (scannedSscc: string, match?: PalletMatch) => {
    if (!selectedPlace || !recordScannedPallet) return;

    if (await runPlaceAction(() => recordScannedPallet(selectedPlace.id, scannedSscc, match))) {
      setUnmatchedSscc(null);
      await loadRecords(selectedPlace.id, scannedSscc);
    }
  }, [selectedPlace, recordScannedPallet, runPlaceAction, loadRecords]);

  const handlePalletMatched = useCallback((
    pallet: InitialInventorizationPallet,
    quantity: number | null
  ) => {
    if (unmatchedSscc) {
      storeScannedPallet(unmatchedSscc, {
        palletId: pallet.id,
        palletExtId: pallet.ext_id,
        orderId: getMatchedPalletOrder(pallet, findIncomingOrderById)?.order_id ?? null,
        quantity,
      });
    }
  }, [unmatchedSscc, storeScannedPallet, findIncomingOrderById]);

  /** The pallet number the operator read off the label belongs to no pallet the system knows. */
  const handlePalletNumberStore = useCallback((
    palletExtId: string,
    quantity: number | null,
    orderId: number | null
  ) => {
    if (unmatchedSscc) {
      storeScannedPallet(unmatchedSscc, { palletExtId, orderId, quantity });
    }
  }, [unmatchedSscc, storeScannedPallet]);

  const handleDeleteRecord = useCallback(async (record: InventorizationStatusData) => {
    if (!selectedPlace || !deleteStatus || !setPlaceStatusFromRecords) return;

    if (await runPlaceAction(() => deleteStatus(record.id))) {
      const remaining = await loadRecords(selectedPlace.id);
      if (remaining) setPlaceStatusFromRecords(selectedPlace.id, remaining);
    }
  }, [selectedPlace, deleteStatus, setPlaceStatusFromRecords, runPlaceAction, loadRecords]);

  const handleConfirmClearPlace = useCallback(async () => {
    setShowClearPlacePending(false);
    if (!selectedPlace || !clearPlace) return;

    // The place starts a fresh cycle: the next scan of it selects rather than reports.
    placeScanStreakRef.current = null;

    if (await runPlaceAction(() => clearPlace(selectedPlace.id))) {
      await loadRecords(selectedPlace.id);
    }
  }, [selectedPlace, clearPlace, runPlaceAction, loadRecords]);

  /**
   * Initial mode: scanning a place code repeatedly walks it through select → report empty →
   * clear, so the operator never needs the manual controls. Scanning anything else — another
   * place or a pallet — breaks the run and the next scan of the place starts at select
   * again. Nothing is compared against the system: a scan records what was actually found.
   */
  const handleInitialScan = useCallback((code: string, scannedPlace: Place | null) => {
    if (scannedPlace) {
      const streak = placeScanStreakRef.current;
      const scanCount = streak?.placeId === scannedPlace.id ? streak.count + 1 : 1;
      placeScanStreakRef.current = { placeId: scannedPlace.id, count: scanCount };

      if (scannedPlace.id !== selectedPlace?.id) onPlaceSelect(scannedPlace);

      if (scanCount === PLACE_SCANS_TO_REPORT_EMPTY) {
        reportPlaceEmpty(scannedPlace.id);
      } else if (scanCount >= PLACE_SCANS_TO_CLEAR) {
        setShowClearPlacePending(true);
      }
      return;
    }

    if (!selectedPlace) {
      reportUnknownCode(code);
      return;
    }

    if (isPalletsDataLoading) {
      setError(translate('osfc.storage_ui.inventorization.pallets_data_loading'));
      return;
    }

    // Without the pallet list a scan can only be recorded as unmatched, which is
    // indistinguishable from a pallet the system genuinely does not know.
    if (hasPalletsDataFailed) {
      setError(translate('osfc.storage_ui.inventorization.pallets_data_failed'));
      return;
    }

    if (isIncomingOrdersLoading) {
      setError(translate('osfc.storage_ui.inventorization.orders_data_loading'));
      return;
    }

    // A pallet the list does not answer for is attributed to an order by hand, and without
    // these there is nothing to attribute it to.
    if (haveIncomingOrdersFailed) {
      setError(translate('osfc.storage_ui.inventorization.orders_data_failed'));
      return;
    }

    const started = tryGS1Resolve(code, async ({ result, error: resolveError }: { result: any; error: any }) => {
      // 400 = not a GS1 code; anything else is a real failure.
      if (resolveError && resolveError?.status !== 400) {
        setError(getErrorMessage(resolveError, translate));
        return;
      }

      const scannedSscc: string | null = result?.sscc ?? null;
      if (!scannedSscc) {
        reportUnknownCode(code);
        return;
      }

      placeScanStreakRef.current = null;

      // Matching by SSCC is the whole point of the pallet list; the pallet number is the
      // fallback for a pallet whose SSCC the system does not carry.
      const pallet = findPalletBySscc(scannedSscc);
      if (pallet) {
        await storeScannedPallet(scannedSscc, {
          palletId: pallet.id,
          palletExtId: pallet.ext_id,
          orderId: getMatchedPalletOrder(pallet, findIncomingOrderById)?.order_id ?? null,
          quantity: pallet.quantity,
        });
      } else {
        setUnmatchedSscc(scannedSscc);
      }
    });
    if (!started) setError(translate('scanner.already_resolving'));
  }, [
    selectedPlace,
    onPlaceSelect,
    reportUnknownCode,
    reportPlaceEmpty,
    storeScannedPallet,
    findPalletBySscc,
    findIncomingOrderById,
    isPalletsDataLoading,
    hasPalletsDataFailed,
    isIncomingOrdersLoading,
    haveIncomingOrdersFailed,
    tryGS1Resolve,
    translate,
  ]);

  const handleScan = useCallback((code: string) => {
    code = code.trim();
    if (!code) return;

    setPlaceNotFound(false);
    setError(null);

    if (pendingConfirm || showClearPlacePending) {
      setError(translate('osfc.storage_ui.inventorization.confirm_in_progress'));
      return;
    }

    // A scan would move the panel to another place under the open record.
    if (detailsRecord) {
      setError(translate('osfc.storage_ui.inventorization.record_dialog.in_progress'));
      return;
    }

    if (unmatchedSscc) {
      setError(translate('osfc.storage_ui.inventorization.pallet_search_in_progress'));
      return;
    }

    // The place list decides what a place code is; the address template is only
    // consulted to explain a code that matches nothing.
    const scannedPlace = findPlace(code);

    if (isInitialMode) {
      handleInitialScan(code, scannedPlace);
      return;
    }

    if (!selectedPlace) { // No place is currently selected
      // No selection: only a place code can be acted on.
      if (scannedPlace) {
        onPlaceSelect(scannedPlace);
      } else {
        reportUnknownCode(code);
      }
    } else if (scannedPlace) { // A place is selected, and a place code was scanned
      const isCurrentPlace = scannedPlace.id === selectedPlace.id;
      setPendingConfirm({
        ...getStatusForPlaceScan(isPlaceEmpty),
        sscc: null,
        scannedCode: scannedPlace.address,
        messageKey: isCurrentPlace
          ? 'osfc.storage_ui.inventorization.confirm_place_empty'
          : 'osfc.storage_ui.inventorization.confirm_switch_place',
        nextPlace: isCurrentPlace ? undefined : scannedPlace,
      });
      return;
    } else { // A place is selected, and a non-place code was scanned
      const started = tryGS1Resolve(code, async ({ result, error: resolveError }: { result: any; error: any }) => {
        // 400 = not a GS1 code; anything else is a real failure.
        if (resolveError && resolveError?.status !== 400) {
          setError(getErrorMessage(resolveError, translate));
          return;
        }

        const scannedSscc: string | null = result?.sscc ?? null;      
        
        if (!scannedSscc) {
          // Ceel numbers are not GS1 codes: accept one only when this place is known to
          // hold it, so an unrecognised string can never auto-submit a status.
          const isKnownCeel = storageEntities.some((entity) => isCodeCeelNumber(entity, code));
          if (!isKnownCeel) {
            reportUnknownCode(code);
            return;
          }        
        }

        const palletCode = scannedSscc ?? code;
        setPendingConfirm({
          ...getStatusForPalletScan(palletCode, isPlaceEmpty, storageEntities),
          sscc: scannedSscc,
          scannedCode: palletCode,
          messageKey: 'osfc.storage_ui.inventorization.confirm_scanned_pallet',
        });
      });
      if (!started) setError(translate('scanner.already_resolving'));
    }    
  }, [
    pendingConfirm,
    showClearPlacePending,
    detailsRecord,
    unmatchedSscc,
    isInitialMode,
    handleInitialScan,
    selectedPlace,
    storageEntities,
    isPlaceEmpty,
    findPlace,
    onPlaceSelect,
    reportUnknownCode,
    tryGS1Resolve,
    translate,
  ]);

  // TODO: investigate if next comments are true
  // The canvas re-renders this panel on every mouse move, so handleScan gets a new
  // identity constantly. Subscribers take it through this ref: a subscription that
  // depended on the identity would unregister and re-register the target page on each
  // render, and the provider disconnects the scanner while the page list is empty.
  const handleScanRef = useRef(handleScan);
  handleScanRef.current = handleScan;
  const scan = useCallback((code: string) => handleScanRef.current(code), []);

  const handleManualCodeSubmit = useCallback(() => {
    const code = manualCode.trim();
    if (!code) return;
    logScan(code);
    scan(code);
    setManualCode('');
  }, [manualCode, logScan, scan]);

  // Keyboard-wedge scanners go through the same classification as the serial one.
  useScannerInput({
    onScan: scan,
    enabled: !gridDialogOpen && !detailsRecord && !unmatchedSscc,
    scannerThreshold: 100,
    contextType: ScannerLogContextType.inventorization,
  });

  const resetPlaceState = useCallback(() => {
    loadRequestRef.current += 1;
    setRecords([]);
    setIsRecordsLoading(false);
    selectRecord(null);
    setDetailsRecord(null);
    setManualCode('');
    setError(null);
    setPendingConfirm(null);
    setShowClearPlacePending(false);
    setUnmatchedSscc(null);
  }, [selectRecord]);

  const handleModeChange = useCallback((next: InventorizationMode) => {
    setUnmatchedSscc(null);
    setMode(next);
  }, []);

  const handleSelectRecord = useCallback((record: InventorizationStatusData) => {
    setError(null);
    selectRecord(record);
    setDetailsRecord(record);
  }, [selectRecord]);

  const handleSaveRecord = useCallback(async (
    record: InventorizationStatusData,
    quantity: number | null,
    note: string | null,
    orderId: number | null
  ) => {
    if (!selectedPlace) return;

    const saved = await runPlaceAction(async () => {
      await configApi.updateInventorizationStatus(
        terminalId, record.id, quantity, note, orderId
      );
    });
    if (!saved) return;

    setDetailsRecord(null);
    await loadRecords(selectedPlace.id, record.sscc);
  }, [selectedPlace, terminalId, runPlaceAction, loadRecords]);

  useEffect(() => {
    resetPlaceState();
    setPlaceNotFound(false);

    if (selectedPlace) {
      loadRecords(selectedPlace.id);
    }
  }, [selectedPlace, resetPlaceState, loadRecords]);

  const handleOpenRemainingPallets = async () => {
    setRemainingDialogOpen(true);
    setIsRemainingLoading(true);
    setIsRemainingGenerating(false);
    setRemainingPreview(null);
    try {
      const preview = await configApi.getRemainingPallets(terminalId);
      setRemainingPreview(preview);
    } catch (err) {
      console.error('Failed to fetch remaining pallets', err);
      setError(getErrorMessage(err, translate));
      setRemainingDialogOpen(false);
    } finally {
      setIsRemainingLoading(false);
    }
  };

  const handleGenerateRemainingPallets = async () => {
    setIsRemainingGenerating(true);
    try {
      const result = await configApi.generateRemainingPallets(terminalId);
      notify(
        translate(
          'osfc.storage_ui.inventorization.remaining_pallets.generated_toast',
          { count: result.created }
        ),
        'info'
      );
      // Keep the generating flag set so the spinner stays during the dialog
      // close transition (resetting it here flashes the table for a moment);
      // the flag is reset on the next dialog open instead
      setRemainingDialogOpen(false);
    } catch (err) {
      console.error('Failed to generate remaining pallets', err);
      notify(getErrorMessage(err, translate), 'warning');
      setIsRemainingGenerating(false);
    }
  };

  useEffect(() => {
    const unregisterPage = registerTargetPage(INVENTORIZATION_TAB, {
      contextType: ScannerLogContextType.inventorization,
      contextId: null,
      terminalId,
    });
    const unregisterScan = registerScanHandler(INVENTORIZATION_TAB, scan);
    return () => {
      unregisterPage();
      unregisterScan();
    };
  }, [registerTargetPage, registerScanHandler, scan, terminalId]);

  const handleConnectScanner = useCallback(async () => {
    const result = await connectScanner();
    if (result.status === ScannerConnectStatus.failed) {
      setError(result.message);
    }
  }, [connectScanner]);

  const getPendingStatusLabel = useCallback(() => {
    if (!pendingConfirm) {
      return '';
    }

    return getDetailedStatusLabel(pendingConfirm.status, pendingConfirm.incorrectStatus, translate);
  }, [pendingConfirm, translate]);

  const getScannerStatusMessage = useCallback(() => {
    if (isConnected) {
      return translate('scanner.notifications.connected');
    }

    if (isReconnecting) {
      return translate('scanner.reconnecting');
    }

    return translate('osfc.storage_ui.inventorization.scanner_disconnected');
  }, [isConnected, isReconnecting, translate]);

  const pendingStatusLabel = getPendingStatusLabel();
  const isSelectedRecordCorrect = selectedRecord?.status === InventorizationStatus.correct;

  return (
    <>
      <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant="h6" style={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
          {translate('osfc.storage_ui.inventorization.title')}
        </Typography>

        <InventorizationModeToggle
          mode={mode}
          onChange={handleModeChange}
          disabled={isLoading || !!pendingConfirm}
        />

        {isQuickMode && (
          <Typography variant="caption" color="textSecondary">
            {translate('osfc.storage_ui.inventorization.quick_mode_hint')}
          </Typography>
        )}

        {isInitialMode && (
          <Typography variant="caption" color="textSecondary">
            {translate('osfc.storage_ui.inventorization.initial_mode_hint')}
          </Typography>
        )}

        {isInitialMode && hasPalletsDataFailed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography variant="body2" style={{ color: theme.palette.error.main, fontWeight: 'bold', flex: 1 }}>
              {translate('osfc.storage_ui.inventorization.pallets_data_failed')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              disabled={isPalletsDataLoading}
              onClick={reloadPalletsData}
            >
              {translate('osfc.buttons.retry')}
            </Button>
          </div>
        )}

        {haveIncomingOrdersFailed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography variant="body2" style={{ color: theme.palette.error.main, fontWeight: 'bold', flex: 1 }}>
              {translate('osfc.storage_ui.inventorization.orders_data_failed')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              disabled={isIncomingOrdersLoading}
              onClick={reloadIncomingOrders}
            >
              {translate('osfc.buttons.retry')}
            </Button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ScannerStatusIndicator connected={isConnected} />
          <Typography variant="body2" color="textSecondary" style={{ flex: 1 }}>
            {getScannerStatusMessage()}
          </Typography>
          {!isDeviceAuthorized && (
            <Button variant="outlined" size="small" color="primary" onClick={handleConnectScanner}>
              {translate('osfc.buttons.connect')}
            </Button>
          )}
        </div>

        {(isLoading || isResolving) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', backgroundColor: theme.palette.action.hover, borderRadius: '4px' }}>
            <CircularProgress size={20} />
            {isLoading && (
              <Typography variant="body2">{translate('osfc.storage_ui.inventorization.saving_status')}</Typography>
            )}
          </div>
        )}

        {error && (
          <Typography variant="body1" style={{
            color: theme.palette.error.main,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: 'bold',
            padding: '8px',
            backgroundColor: theme.palette.error.light,
            borderRadius: '4px'
          }}>
            <Error /> {error}
          </Typography>
        )}

        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ListIcon />}
          onClick={() => setGridDialogOpen(true)}
          fullWidth
        >
          {translate('osfc.storage_ui.inventorization.view_all_places_grid')}
        </Button>

        {!isQuickMode && (
          <div style={{ display: 'flex', gap: '5px' }}>
              <TextField
                label={translate('osfc.storage_ui.inventorization.scan_search_place')}
                variant="outlined"
                size="small"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setPlaceNotFound(false);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                fullWidth
              />
              <Button variant="contained" color="primary" onClick={handleSearchSubmit}>
                  <Search />
              </Button>
          </div>
        )}

        {placeNotFound && (
          <Typography variant="body1" style={{
            color: theme.palette.error.main,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontWeight: 'bold'
          }}>
            <Error /> {translate('osfc.storage_ui.inventorization.place_not_found')}
          </Typography>
        )}

      {selectedPlace ? (
        <>
            <Typography component="div" variant="body1" style={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                <div>{translate('osfc.storage_ui.inventorization.selected', { address: selectedPlace.address })}</div>
                {storageEntities.length > 0 && (
                    <>
                        {palletSSCCs.length > 0 && (
                            <div>{translate('osfc.storage_ui.inventorization.sscc_label', { value: palletSSCCs.join(', ') })}</div>
                        )}
                        {ceelNumbers.length > 0 && (
                            <div>{translate('osfc.storage_ui.inventorization.ceel_label', { value: ceelNumbers.join(', ') })}</div>
                        )}
                        {storageEntities[0] && (
                            <>
                                <div>{translate('osfc.storage_ui.inventorization.product_label', { value: storageEntities[0].product_name ?? translate('osfc.storage_ui.shared.missing_value') })}</div>
                                <div>{translate('osfc.storage_ui.inventorization.customer_label', { value: storageEntities[0].customer_name ?? translate('osfc.storage_ui.shared.missing_value') })}</div>
                                <div>{translate('osfc.storage_ui.inventorization.boxes_label', { value: storageEntities.reduce((sum, e) => sum + (e.boxes ?? 0), 0) })}</div>
                            </>
                        )}
                    </>
                )}
            </Typography>

            <Button variant="outlined" onClick={onPlaceDeselect} fullWidth disabled={isLoading}>
                {translate('osfc.storage_ui.inventorization.deselect')}
            </Button>

            {/* The same reset the third scan of a place in a row asks for. */}
            {isInitialMode && clearPlace && (
                <Button
                    variant="outlined"
                    onClick={() => setShowClearPlacePending(true)}
                    fullWidth
                    disabled={isLoading || !records.length}
                    style={{ color: theme.palette.error.main, borderColor: theme.palette.error.main }}
                >
                    {translate('osfc.storage_ui.inventorization.clear_place')}
                </Button>
            )}

            {!isInitialMode && selectedRecord?.status && (
                <div style={{
                    padding: '8px',
                    backgroundColor: isSelectedRecordCorrect
                      ? theme.palette.success.light
                      : theme.palette.error.light,
                    borderRadius: '4px',
                    border: `1px solid ${isSelectedRecordCorrect
                      ? theme.palette.success.main
                      : theme.palette.error.main}`,
                    color: isSelectedRecordCorrect
                      ? theme.palette.success.dark
                      : theme.palette.error.dark,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px'
                }}>
                    <Typography variant="body1" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold' }}>
                        {isSelectedRecordCorrect ? <CheckCircle /> : <Error />}
                        {getStatusLabel(selectedRecord.status, translate)}
                    </Typography>
                    {!isSelectedRecordCorrect && selectedRecord.incorrect_status && (
                        <Typography variant="body1" style={{ marginLeft: '24px' }}>
                            {getIncorrectStatusLabel(selectedRecord.incorrect_status, translate)}
                        </Typography>
                    )}
                </div>
            )}

            <InventorizationRecordsList
              records={records}
              selectedId={selectedRecordId}
              disabled={isLoading}
              isLoading={isRecordsLoading}
              showPalletMatch={isInitialMode}
              onSelect={handleSelectRecord}
              onDelete={isInitialMode ? handleDeleteRecord : undefined}
            />

            {/* Initial mode writes a fixed status with no note, so there is nothing to edit. */}
            {!isInitialMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <TextField
                  label={translate('osfc.storage_ui.inventorization.note_label')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={8}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={isQuickMode ? undefined : translate('osfc.storage_ui.inventorization.note_placeholder')}
                  helperText={isQuickMode
                    ? undefined
                    : translate('osfc.storage_ui.inventorization.note_characters', { count: note.length, max: NOTE_MAX_LENGTH })}
                  inputProps={{ maxLength: NOTE_MAX_LENGTH, readOnly: isQuickMode }}
                />

                <TextField
                  label={translate('osfc.storage_ui.inventorization.sscc_field_label')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={sscc}
                  onChange={(e) => setSscc(e.target.value.trim())}
                  error={!isQuickMode && !!sscc && !CANONICAL_SSCC_RE.test(sscc)}
                  helperText={!isQuickMode && !!sscc && !CANONICAL_SSCC_RE.test(sscc)
                    ? translate('osfc.storage_ui.inventorization.invalid_sscc')
                    : undefined}
                  inputProps={{ maxLength: SSCC_LENGTH, readOnly: isQuickMode }}
                />

                {!isQuickMode && MANUAL_STATUS_OPTIONS.map((option) => (
                  <Button
                    key={`${option.status}-${option.incorrectStatus ?? ''}`}
                    variant="contained"
                    style={{
                      backgroundColor: getStatusColor(option.status, theme),
                      color: theme.palette.common.white,
                    }}
                    onClick={() => handleManualStatus(option.status, option.incorrectStatus)}
                    fullWidth
                    disabled={isLoading}
                  >
                      {getDetailedStatusLabel(option.status, option.incorrectStatus, translate)}
                  </Button>
                ))}
            </div>
            )}

            {isNormalMode && (
              <InfoBox title={translate('osfc.storage_ui.inventorization.verify_pallet')}>
                  <TextField
                      label={translate('osfc.storage_ui.inventorization.scan_pallet')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleManualCodeSubmit()}
                      helperText={translate('osfc.storage_ui.inventorization.scan_helper')}
                  />
                  <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={handleManualCodeSubmit}
                      style={{ marginTop: '8px' }}
                      disabled={isLoading}
                  >
                      {translate('osfc.storage_ui.inventorization.check')}
                  </Button>
              </InfoBox>
            )}

            <PlaceInfo selectedPlaces={selectedPlaces} terminalId={terminalId} storageEntities={storageEntities} hideAssign={true} />
        </>
      ) : (
        <Typography variant="body1" color="textSecondary">
            {translate('osfc.storage_ui.inventorization.scan_or_select')}
        </Typography>
      )}

        {canGenerateRemainingPallets && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOpenRemainingPallets}
            fullWidth
            style={{ marginTop: 'auto' }}
          >
            {translate('osfc.storage_ui.inventorization.remaining_pallets.generate_button')}
          </Button>
        )}
      </div>

      <InventorizationGridDialog
        open={gridDialogOpen}
        onClose={() => setGridDialogOpen(false)}
        terminalId={terminalId}
      />
      <InitialPalletSearchDialog
        sscc={unmatchedSscc}
        disabled={isLoading}
        error={error}
        findPalletByExtId={findPalletByExtId}
        isExtIdAmbiguous={isExtIdAmbiguous}
        incomingOrders={incomingOrders}
        findIncomingOrderById={findIncomingOrderById}
        onMatched={handlePalletMatched}
        onStore={handlePalletNumberStore}
        onCancel={() => setUnmatchedSscc(null)}
      />
      <InventorizationRecordDialog
        record={detailsRecord}
        placeAddress={selectedPlace?.address}
        disabled={isLoading}
        error={error}
        incomingOrders={incomingOrders}
        findIncomingOrderById={findIncomingOrderById}
        onSave={handleSaveRecord}
        onClose={() => setDetailsRecord(null)}
      />
      <RemainingPalletsDialog
        open={remainingDialogOpen}
        isLoading={isRemainingLoading}
        isGenerating={isRemainingGenerating}
        data={remainingPreview}
        onClose={() => setRemainingDialogOpen(false)}
        onGenerate={handleGenerateRemainingPallets}
      />
      <Confirm
        isOpen={showClearPlacePending}
        loading={isLoading}
        title="osfc.storage_ui.inventorization.clear_place_title"
        content="osfc.storage_ui.inventorization.clear_place_message"
        translateOptions={{ address: selectedPlace?.address ?? '' }}
        confirmColor="warning"
        onConfirm={handleConfirmClearPlace}
        onClose={() => setShowClearPlacePending(false)}
      />
      <TimedConfirmDialog
        open={!!pendingConfirm}
        title={translate('osfc.storage_ui.inventorization.confirm_title')}
        message={pendingConfirm && (
          <>
            <div>{translate(pendingConfirm.messageKey, { code: pendingConfirm.scannedCode })}</div>
            <div style={{ fontWeight: 'bold', marginTop: '8px' }}>{pendingStatusLabel}</div>
          </>
        )}
        confirmLabel={pendingStatusLabel}
        autoConfirmMs={isQuickMode ? QUICK_AUTO_CONFIRM_MS : NORMAL_AUTO_CONFIRM_MS}
        onConfirm={handleConfirmPending}
        onCancel={handleCancelPending}
      />
    </>
  );
};
